import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchPrincipal, fetchRole, type AuthUser } from '../lib/auth';

export interface UseAuthResult {
  user: AuthUser | null;
  isLoading: boolean;
  /** True while the Editor role is still resolving (the user renders as a Viewer). */
  isRoleLoading: boolean;
}

/**
 * The signed-in identity from the SWA edge. Fast (~50ms) and the only thing the
 * router gates on — see the header comment in lib/auth.ts.
 */
export const principalQuery = {
  queryKey: ['auth', 'principal'] as const,
  queryFn: fetchPrincipal,
  staleTime: 5 * 60_000,
  // The edge either knows the session or it doesn't; a failure here means
  // "signed out", so don't sit on a splash retrying it.
  retry: 1,
};

/**
 * The Editor role from `/api/me`. Slow on a cold stack (Flex Consumption cold
 * start + a possible Azure SQL serverless resume), so it resolves in the
 * background rather than blocking the app.
 */
export const roleQuery = {
  queryKey: ['auth', 'role'] as const,
  queryFn: fetchRole,
  staleTime: 5 * 60_000,
  // Retry with backoff: the role lookup hits a Flex Consumption Function App
  // that cold-starts, so the first call after an idle period can fail
  // transiently. Retrying absorbs that instead of downgrading to Viewer.
  retry: 4,
  retryDelay: (attempt: number) => Math.min(800 * 2 ** attempt, 8000),
};

/**
 * Kick the auth requests off at startup, before React mounts, so the Function
 * cold start and any SQL resume overlap the bundle's own render work instead of
 * starting after it.
 *
 * The role fetch waits on the principal only so a signed-out visitor doesn't
 * fire (and then retry) a `/api/me` that can't succeed. That costs the ~50ms
 * edge round trip, which is noise next to the cold start it front-runs.
 */
export function prefetchAuth(qc: QueryClient): void {
  void qc.prefetchQuery(principalQuery).then(() => {
    if (qc.getQueryData(principalQuery.queryKey) != null) {
      void qc.prefetchQuery(roleQuery);
    }
  });
}

/**
 * Reads the signed-in user. Drives route gating and edit-affordance
 * visibility. Server endpoints re-check roles independently.
 *
 * `isLoading` tracks only the principal — the app renders as soon as we know
 * *who* the user is. `isEditor` starts `false` and flips true when the role
 * query lands, so edit controls fade in a moment after the first paint on a
 * cold load. Writes are enforced server-side, so that gap is cosmetic.
 */
export function useAuth(): UseAuthResult {
  const principal = useQuery(principalQuery);
  // Only ask the API who we are once the edge says we're signed in.
  const role = useQuery({ ...roleQuery, enabled: principal.data != null });

  const user: AuthUser | null = principal.data
    ? {
        userId: principal.data.userId,
        email: role.data?.email || principal.data.email,
        roles: principal.data.roles,
        isEditor: role.data?.isEditor ?? false,
      }
    : null;

  return { user, isLoading: principal.isLoading, isRoleLoading: role.isPending };
}
